/**
 * SyncStatusSection — Shopify order-sync health, delta compare, and manual
 * sync trigger (extracted from the old god component's `renderSyncStatus`).
 *
 * Diagnostics, not settings: inline action buttons stay, no dirty
 * registration — this section only ever reads/refreshes live status.
 */
import { useState } from 'react';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';

export function SyncStatusSection() {
  // No store attached → order sync doesn't apply. Show a neutral note rather
  // than a permanent "Never synced" / spinning disabled query.
  const { organization } = useOrganization();
  const hasShopifyStore = !!organization?.shopify_domain;
  // Sync health moves on the order-sync cron's 5 minute cadence, so polling it
  // every 30s re-counted the org's recent orders four times per change. Run
  // Sync Now refetches on success, so the operator-driven path stays instant.
  const syncStatusQuery = trpc.productionSettings.getSyncStatus.useQuery(undefined, {
    enabled: hasShopifyStore,
    refetchInterval: 120_000,
    staleTime: 120_000,
  });
  const [deltaWindow, setDeltaWindow] = useState<24 | 168>(24);
  const [syncLookback, setSyncLookback] = useState<string>('default');
  const checkDelta = trpc.productionSettings.checkShopifyDelta.useMutation();
  const triggerSync = trpc.productionSettings.triggerSync.useMutation({
    onSuccess: () => {
      syncStatusQuery.refetch();
      toast.success('Sync triggered', { description: 'Status will update shortly' });
    },
    onError: (err) => toast.error('Failed to trigger sync', { description: err.message }),
  });

  const sync = syncStatusQuery.data;
  const delta = checkDelta.data;

  // Local helper: relative timestamp with status dot
  const SyncTimestamp = ({ timestamp, ordersSynced }: { timestamp: string | null; ordersSynced?: number | null }) => {
    if (!timestamp) return <span className="text-sm text-muted-foreground">Never synced</span>;
    const date = new Date(timestamp);
    const minutesAgo = (Date.now() - date.getTime()) / 60_000;
    const isStale = minutesAgo > 15;
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${isStale ? 'bg-amber-500' : 'bg-green-500'}`} />
        <span className="text-muted-foreground">
          Last synced {formatDistanceToNow(date, { addSuffix: true })}
          {ordersSynced != null && ` (${ordersSynced} orders)`}
        </span>
      </span>
    );
  };

  // Local helper: stat pill
  const SyncStat = ({ label, value, icon: Icon }: { label: string; value: string | number; icon: typeof Activity }) => (
    <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3 py-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="font-medium tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );

  return (
    <SettingsSection id="sync-status" title="Order sync" icon={RefreshCw}>
      {!hasShopifyStore ? (
        <p className="text-sm text-muted-foreground py-2">
          No Shopify store is connected to this organisation, so order sync does not apply.
        </p>
      ) : (
        <div className="space-y-4">
          {/* Loading state */}
          {syncStatusQuery.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sync ? (
            <>
              {/* Sync health row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Running / Idle badge */}
                {sync.isRunning ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Syncing...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Idle
                  </span>
                )}

                <SyncTimestamp timestamp={sync.lastSyncedAt} ordersSynced={sync.ordersSynced} />

                <div className="sm:ml-auto flex items-center gap-2">
                  <Select value={syncLookback} onValueChange={setSyncLookback}>
                    <SelectTrigger className="w-[160px] h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">From checkpoint</SelectItem>
                      <SelectItem value="60">Last 1 hour</SelectItem>
                      <SelectItem value="360">Last 6 hours</SelectItem>
                      <SelectItem value="1440">Last 24 hours</SelectItem>
                      <SelectItem value="10080">Last 7 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lookbackMinutes = syncLookback !== 'default' ? Number(syncLookback) : undefined;
                      triggerSync.mutate(lookbackMinutes ? { lookbackMinutes } : undefined);
                    }}
                    disabled={triggerSync.isPending || sync.isRunning}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${(triggerSync.isPending || sync.isRunning) ? 'animate-spin' : ''}`} />
                    {sync.isRunning ? 'Syncing...' : 'Run Sync Now'}
                  </Button>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SyncStat icon={Activity} label="orders synced" value={sync.ordersSynced ?? '-'} />
                <SyncStat icon={FileText} label="pages" value={sync.pagesSynced ?? '-'} />
                <SyncStat icon={Clock} label="local 24h" value={sync.localOrderCount24h} />
                <SyncStat icon={Clock} label="local 7d" value={sync.localOrderCount7d} />
              </div>

              {/* Error banner */}
              {sync.lastError && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-800">Last sync error</p>
                    <p className="text-xs text-amber-700/80 break-words mt-0.5">{sync.lastError}</p>
                  </div>
                </div>
              )}

              <Separator />

              {/* Shopify comparison section */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Compare with Shopify</p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <Select
                    value={String(deltaWindow)}
                    onValueChange={(v) => setDeltaWindow(Number(v) as 24 | 168)}
                  >
                    <SelectTrigger className="w-full sm:w-[140px] h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">Last 24 hours</SelectItem>
                      <SelectItem value="168">Last 7 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => checkDelta.mutate({ windowHours: deltaWindow })}
                    disabled={checkDelta.isPending}
                    className="h-11 gap-2"
                  >
                    {checkDelta.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    Check Shopify
                  </Button>
                </div>

                {/* Delta error */}
                {checkDelta.isError && (
                  <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-sm text-red-800">{checkDelta.error.message}</p>
                  </div>
                )}

                {/* Delta results */}
                {delta && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <span>Shopify: <strong className="tabular-nums">{delta.shopifyOrderCount}</strong></span>
                      <span>Local DB: <strong className="tabular-nums">{delta.localOrderCount}</strong></span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        delta.delta === 0
                          ? 'bg-green-100 text-green-700'
                          : delta.delta <= 5
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {delta.delta === 0 ? (
                          <><CheckCircle2 className="h-3 w-3" /> Match</>
                        ) : (
                          <><AlertCircle className="h-3 w-3" /> {delta.delta} missing</>
                        )}
                      </span>
                      {delta.delta > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const lookbackMinutes = deltaWindow * 60;
                            triggerSync.mutate({ lookbackMinutes });
                          }}
                          disabled={triggerSync.isPending || syncStatusQuery.data?.isRunning}
                          className="gap-1.5 h-7 text-xs"
                        >
                          <RefreshCw className={`h-3 w-3 ${(triggerSync.isPending || syncStatusQuery.data?.isRunning) ? 'animate-spin' : ''}`} />
                          Sync Missing
                        </Button>
                      )}
                    </div>

                    {delta.missingOrdersSample.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">
                          Missing orders ({delta.missingOrderCount > 10 ? `showing 10 of ${delta.missingOrderCount}` : delta.missingOrderCount})
                        </p>
                        <div className="space-y-1">
                          {delta.missingOrdersSample.map((order) => (
                            <div key={order.shopifyGid} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1.5">
                              <span className="font-mono font-medium">{order.name ?? order.shopifyGid}</span>
                              <span className="text-muted-foreground">
                                {formatDistanceToNow(new Date(order.createdAt), { addSuffix: true })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {delta.truncated && (
                      <p className="text-xs text-amber-600">
                        Results truncated: more than 10,000 orders in this window.
                      </p>
                    )}

                    <p className="text-xs text-muted-foreground">
                      Checked {formatDistanceToNow(new Date(delta.checkedAt), { addSuffix: true })}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </SettingsSection>
  );
}
